import { useCallback, useContext, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import LogoSpinnerInline from "../../../../academic/examinations/spinner";
import React from "react";
import { useUserPermissions } from "../../../../UserPermissionsContext";
import { InactivityContext } from "../../InactivityContext";
import { motion, Variants } from "framer-motion";
import Cookies from "js-cookie";
import ImageWithBasePath from "../../../../../core/common/imageWithBasePath";
import CountUp from "react-countup";

import {
    ResponsiveContainer,
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
} from "recharts";
import { Select } from "antd";
import { BiRefresh } from "react-icons/bi";
import Counter from "../../counter";
type FilterType = "overall" | "today" | "week" | "month";

type CardItem = {
    id: string;
    logo: string;
    heading: string;
    heading_value: number;
    percentage: number;
    sub_heading_1: string;
    sub_heading_1_value: number;
    sub_heading_2: string;
    sub_heading_2_value: number;
};

interface Cards {
    id: number;
    heading: string;
    type: number;
    type_name: string;
    section: number;
    section_name: string;
    logo: string;
    sequence: number;
    heading_value: string;
    sub_heading_1: string;
    sub_heading_1_value: string;
    sub_heading_2: string;
    sub_heading_2_value: string;
    percentage: string;
}

interface WeeklyCallData {
    date: string;
    day: string;
    total_calls: number;
    connected_calls: number;
    total_call_duration: number;
    conversation_duration: number;
}

interface WeeklyCallsResponse {
    week_start: string;
    week_end: string;
    results: WeeklyCallData[];
}

type Dashboard = {
    id: number;
    agent_name: string;
    status_name: string;
    updated_at: string;
    last_login_time: string;
    agent: number;
    status: number;
    calls: string;
    connected: string;
    missed: string;
    interested: string;
    callbacks: string;
    sale: string;
    performance: string;
    total_ideal_time: string;
    conversation_time: string;
    total_calls: string;
    connected_calls: string;
    total_call_duration: string;
    monitoring_status: string;
    profile_pic: string;
    name: string;
};

const MetaOnBoardDashboard = () => {
    const navigate = useNavigate();

    const jwtToken = Cookies.get("authToken");

    const [photoLoading, setPhotoLoading] = useState(false);

    const [filter, setFilter] = useState<FilterType>("overall");
    const [selectedStatus, setSelectedStatus] = useState<any>("");
    const [selectedAgents, setSelectedAgents] = useState<number[]>([]);
    const [refreshIsLoading, seteRfreshIsLoading] = useState(false);
    const [dashboardData, setDashboardData] = useState<Dashboard[]>([]);
    const [monitoringStatus, setMonitoringStatus] = useState<
        { id: number; name: string }[]
    >([]);

    const [bgGradient, setBgGradient] = React.useState(
        "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
    );

    const [loading, setLoading] = useState(false);

    const [cards, setCards] = useState<Cards[]>([]);

    // Weekly calls graph state
    const [weeklyCalls, setWeeklyCalls] = useState<WeeklyCallData[]>([]);
    const [weeklyCallsLoading, setWeeklyCallsLoading] = useState(false);

    const { userPermissions, webApi, userData } = useUserPermissions();

    const inactivityContext = useContext(InactivityContext);

    if (!inactivityContext) {
        throw new Error("Header must be used within an InactivityProvider");
    }

    const { photo1 } = inactivityContext;
    const context = useContext(InactivityContext);
    if (!context) {
        throw new Error("SomeComponent must be used within an InactivityProvider");
    }

    const { isIdle, isStatusChange, setIsStatusChange } = context;
    // ----------------------------------------
    // Extract profile image color
    // ----------------------------------------

    const extractColor = (img: HTMLImageElement) => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        if (!ctx) return;

        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;

        ctx.drawImage(img, 0, 0);

        const { data } = ctx.getImageData(
            0,
            0,
            canvas.width,
            canvas.height
        );

        let r = 0;
        let g = 0;
        let b = 0;
        let count = 0;

        for (let i = 0; i < data.length; i += 4 * 20) {
            r += data[i];
            g += data[i + 1];
            b += data[i + 2];
            count++;
        }

        r = Math.floor(r / count);
        g = Math.floor(g / count);
        b = Math.floor(b / count);

        setBgGradient(
            `linear-gradient(135deg, rgb(${r},${g},${b}) 0%, rgba(${r},${g},${b},0.6) 100%)`
        );
    };

    // ----------------------------------------
    // Dashboard title
    // ----------------------------------------

    const getTitle = (filter: FilterType) => {
        switch (filter) {
            case "today":
                return "Today Matrix";

            case "week":
                return "Week Matrix";

            case "month":
                return "Month Matrix";

            default:
                return "Overall Matrix";
        }
    };

    // ----------------------------------------
    // Format cards data
    // ----------------------------------------

    const formatCardsData = (data: any): CardItem[] => {
        if (!data) return [];

        return [
            {
                id: "students",
                logo: "/assets/img/students_2995620.png",
                heading: "Students",
                heading_value: data?.students?.total_students ?? 0,
                percentage: data?.students?.students_per ?? 0,
                sub_heading_1: "Connected",
                sub_heading_1_value:
                    data?.students?.connected_students ?? 0,
                sub_heading_2: "Remaining",
                sub_heading_2_value:
                    data?.students?.remaining_students ?? 0,
            },
            {
                id: "calls",
                logo: "/assets/img/phone-call_1759915.png",
                heading: "Calls",
                heading_value: data?.calls?.total_calls ?? 0,
                percentage: data?.calls?.calls_per ?? 0,
                sub_heading_1: "Connected",
                sub_heading_1_value:
                    data?.calls?.connected_calls ?? 0,
                sub_heading_2: "Missed",
                sub_heading_2_value:
                    data?.calls?.missed_calls ?? 0,
            },
        ];
    };

    // ----------------------------------------
    // Card animation
    // ----------------------------------------

    const cardVariants: Variants = {
        hidden: {
            opacity: 0,
            y: 30,
        },

        visible: (index: number) => ({
            opacity: 1,
            y: 0,

            transition: {
                delay: index * 0.1,
                duration: 0.5,
                ease: [0.25, 0.1, 0.25, 1],
            },
        }),
    };

    // ----------------------------------------
    // API options
    // ----------------------------------------

    const options = {
        method: "GET",

        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwtToken}`,
        },
    };

    // ----------------------------------------
    // Fetch cards
    // ----------------------------------------

    const fetchCards = async (filter: FilterType) => {
        setLoading(true);

        try {
            const url =
                filter === "overall"
                    ? `${webApi}/meta_onboard/get_dashboard_cards_data/`
                    : `${webApi}/meta_onboard/get_dashboards_cards_with_date_filter_data/?date_filter=${filter}`;

            const response = await fetch(url, options);

            if (!response.ok) {
                const errorData = await response.json();

                if (errorData.code === "token_not_valid") {
                    navigate("/login");
                    return;
                }

                throw new Error(
                    errorData.detail || "Failed to fetch card data"
                );
            }

            const data = await response.json();

            setCards(data);
        } catch (error) {
            console.error("Cards API error:", error);
        } finally {
            setLoading(false);
        }
    };

    // ----------------------------------------
    // Fetch weekly calls graph data
    // ----------------------------------------

    const fetchWeeklyCalls = async () => {
        setWeeklyCallsLoading(true);

        try {
            const url =
                `${webApi}/meta_onboard/get_weekly_calls_graph_data/`;

            const response = await fetch(url, options);

            if (!response.ok) {
                const errorData = await response.json();

                if (errorData.code === "token_not_valid") {
                    navigate("/login");
                    return;
                }

                throw new Error(
                    errorData.detail ||
                    "Failed to fetch weekly calls data"
                );
            }

            const data: WeeklyCallsResponse =
                await response.json();

            setWeeklyCalls(data?.results ?? []);
        } catch (error) {
            console.error(
                "Weekly calls API error:",
                error
            );

            setWeeklyCalls([]);
        } finally {
            setWeeklyCallsLoading(false);
        }
    };

    // ----------------------------------------
    // Fetch cards when filter changes
    // ----------------------------------------

    useEffect(() => {
        fetchCards(filter);
    }, [filter]);

    // ----------------------------------------
    // Fetch weekly graph only once
    // ----------------------------------------

    useEffect(() => {
        fetchWeeklyCalls();
        fetchData();
        fetchMonitoringStatusDropdown();
    }, []);

    // ----------------------------------------
    // Format numbers
    // ----------------------------------------

    const formatINR = useCallback(
        (value: number) => value.toLocaleString("en-IN"),
        []
    );

    // ----------------------------------------
    // Graph data
    // ----------------------------------------

    const graphData = weeklyCalls.map((item) => ({
        ...item,

        // X-axis display value
        dayLabel: item.day,

        // Make sure API values are numbers
        totalCalls: Number(item.total_calls ?? 0),

        connectedCalls: Number(
            item.connected_calls ?? 0
        ),

        totalCallDuration: Number(
            item.total_call_duration ?? 0
        ),

        conversationDuration: Number(
            item.conversation_duration ?? 0
        ),
    }));

    const fetchMonitoringStatusDropdown = async () => {
        const response = await fetch(
            `${webApi}/user_monitoring/monitoring_status_types_dropdown/`,
            options
        );
        const data = await response.json();
        setMonitoringStatus(data);
    };

    const fetchData = async () => {
        seteRfreshIsLoading(true); // Set loading to true
        try {
            const response = await fetch(
                `${webApi}/meta_onboard/agent_activity_monitoring/`,
                options
            );
            const data = await response.json();
            setDashboardData(data.results);
        } catch (error) {
            console.error("Failed to fetch data:", error);
        } finally {
            seteRfreshIsLoading(false); // Set loading back to false after fetch is complete
        }
    };

    const handleStatusChange = async (value: any) => {
        const statusId = value;
        setSelectedStatus(statusId);

        if (selectedAgents.length > 0 && statusId) {
            const payload = {
                user_ids: selectedAgents,
            };

            try {
                const response = await fetch(
                    `${webApi}/user_monitoring/change_multiple_agents_status/${statusId}/`,
                    {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${jwtToken}`,
                        },
                        body: JSON.stringify(payload),
                    }
                );

                if (response.ok) {
                    console.log("Status updated successfully");
                    setIsStatusChange(true);
                    setSelectedAgents([]);
                    setSelectedStatus("");
                    fetchData();
                } else {
                    console.error("Failed to update status");
                }
            } catch (error) {
                console.error("Error:", error);
            }
        }
    };

    const handleCheckboxChange = (agentId: any) => {
        setSelectedAgents((prevSelected) => {
            if (prevSelected.includes(agentId)) {
                return prevSelected.filter((id) => id !== agentId);
            } else {
                return [...prevSelected, agentId];
            }
        });
    };


    return (
        <>
            {/* Page Wrapper */}

            <div className="page-wrapper">
                <div className="content">

                    {/* -------------------------------- */}
                    {/* Page Header */}
                    {/* -------------------------------- */}

                    <div className="d-md-flex d-block align-items-center justify-content-between mb-3">

                        <div className="my-auto mb-2">

                            <h3 className="page-title mb-1">
                                Meta Onboard Dashboard
                            </h3>

                            <nav>
                                <ol className="breadcrumb mb-0">

                                    <li className="breadcrumb-item">
                                        <Link to="#">
                                            Dashboard
                                        </Link>
                                    </li>

                                    <li
                                        className="breadcrumb-item active"
                                        aria-current="page"
                                    >
                                        Meta Onboard Dashboard
                                    </li>

                                </ol>
                            </nav>

                        </div>

                        <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">

                            <div className="mb-2">
                                {/* Add button can be added here */}
                            </div>

                            <div className="mb-2">
                                {/* Other buttons can be added here */}
                            </div>

                        </div>

                    </div>

                    {/* -------------------------------- */}
                    {/* Welcome Banner */}
                    {/* -------------------------------- */}

                    <div className="row">

                        {photoLoading ? (

                            <div
                                style={{
                                    height: "100%",
                                    width: "100%",
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                }}
                            >
                                <LogoSpinnerInline />
                            </div>

                        ) : (

                            <div className="col-md-12 d-flex">

                                <div
                                    className="card flex-fill"
                                    style={{
                                        background: bgGradient,
                                        border: "none",
                                        transition:
                                            "background 0.4s ease",
                                    }}
                                >

                                    <div
                                        className="card-body"
                                        style={{
                                            position: "relative",
                                        }}
                                    >

                                        <h1 className="text-white mb-1">
                                            Welcome{" "}
                                            {userData?.username?.toLowerCase()}
                                        </h1>

                                        <p className="text-white mb-3">
                                            Have a Good day at work
                                        </p>

                                        <div
                                            style={{
                                                position: "absolute",
                                                right: 0,
                                                top: 0,
                                                height: "100%",
                                                aspectRatio: "56 / 56",
                                                maxWidth: "45%",
                                                overflow: "hidden",
                                                pointerEvents: "none",
                                            }}
                                        >

                                            <img
                                                src={photo1}
                                                alt="User Profile"
                                                crossOrigin="anonymous"
                                                onLoad={(e) =>
                                                    extractColor(
                                                        e.currentTarget
                                                    )
                                                }
                                                style={{
                                                    width: "100%",
                                                    height: "100%",
                                                    objectFit: "cover",

                                                    filter:
                                                        "brightness(1.08) contrast(1.15) saturate(1.15)",

                                                    WebkitMaskImage:
                                                        "linear-gradient(to left, rgba(0,0,0,1), rgba(0,0,0,0.7))",

                                                    maskImage:
                                                        "linear-gradient(to left, rgba(0,0,0,1), rgba(0,0,0,0))",
                                                }}
                                            />

                                        </div>

                                    </div>

                                </div>

                            </div>

                        )}

                    </div>

                    {/* -------------------------------- */}
                    {/* Matrix Header */}
                    {/* -------------------------------- */}

                    <div className="row">

                        <div className="d-flex align-items-center justify-content-between flex-wrap">

                            {/* LEFT */}

                            <h3 className="page-title mb-2">
                                {getTitle(filter)}
                            </h3>

                            {/* RIGHT */}

                            <div
                                className="mb-2"
                                style={{
                                    minWidth: "150px",
                                }}
                            >

                                <select
                                    className="form-select"
                                    value={filter}
                                    onChange={(
                                        e: React.ChangeEvent<HTMLSelectElement>
                                    ) =>
                                        setFilter(
                                            e.target.value as FilterType
                                        )
                                    }
                                >

                                    <option value="overall">
                                        Overall
                                    </option>

                                    <option value="today">
                                        Today
                                    </option>

                                    <option value="week">
                                        Week
                                    </option>

                                    <option value="month">
                                        Month
                                    </option>

                                </select>

                            </div>

                        </div>

                        {/* -------------------------------- */}
                        {/* Cards */}
                        {/* -------------------------------- */}

                        {loading ? (

                            <div
                                style={{
                                    height: "200px",
                                    width: "100%",
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                }}
                            >
                                <LogoSpinnerInline />
                            </div>

                        ) : (

                            formatCardsData(cards).map(
                                (
                                    card: CardItem,
                                    index: number
                                ) => (

                                    <div
                                        key={card.id}
                                        className="col-xxl-3 col-sm-6 d-flex"
                                    >

                                        <motion.div
                                            className="card flex-fill animate-card border-0"

                                            variants={
                                                cardVariants
                                            }

                                            initial="hidden"

                                            animate="visible"

                                            custom={index}

                                            whileHover={{
                                                y: -6,
                                                scale: 1.02,
                                                boxShadow:
                                                    "0px 10px 25px rgba(0,0,0,0.12)",
                                            }}
                                        >

                                            <div className="card-body">

                                                {/* Top */}

                                                <div className="d-flex align-items-center">

                                                    <div className="avatar avatar-xl me-2 p-1">

                                                        <ImageWithBasePath
                                                            src={card.logo}
                                                            alt={
                                                                card.heading
                                                            }
                                                        />

                                                    </div>

                                                    <div className="overflow-hidden flex-fill">

                                                        <div className="d-flex align-items-center justify-content-between">

                                                            <h2 className="counter">

                                                                <CountUp
                                                                    end={Number(
                                                                        card.heading_value
                                                                    )}
                                                                    formattingFn={
                                                                        formatINR
                                                                    }
                                                                />

                                                            </h2>

                                                            <span
                                                                className={`badge ${Number(
                                                                    card.percentage
                                                                ) < 25
                                                                    ? "bg-danger"
                                                                    : Number(
                                                                        card.percentage
                                                                    ) < 50
                                                                        ? "bg-warning"
                                                                        : Number(
                                                                            card.percentage
                                                                        ) < 75
                                                                            ? "bg-primary"
                                                                            : "bg-success"
                                                                    }`}
                                                            >
                                                                {
                                                                    card.percentage
                                                                }
                                                                %
                                                            </span>

                                                        </div>

                                                        <p>
                                                            {
                                                                card.heading
                                                            }
                                                        </p>

                                                    </div>

                                                </div>

                                                {/* Bottom */}

                                                <div className="d-flex align-items-center justify-content-between border-top mt-3 pt-3">

                                                    <p className="mb-0">

                                                        {
                                                            card.sub_heading_1
                                                        }{" "}
                                                        :

                                                        <span className="text-dark fw-semibold">

                                                            {
                                                                card.sub_heading_1_value
                                                            }

                                                        </span>

                                                    </p>

                                                    <span className="text-light">
                                                        |
                                                    </span>

                                                    <p className="mb-0">

                                                        {
                                                            card.sub_heading_2
                                                        }{" "}
                                                        :

                                                        <span className="text-dark fw-semibold">

                                                            {
                                                                card.sub_heading_2_value
                                                            }

                                                        </span>

                                                    </p>

                                                </div>

                                            </div>

                                        </motion.div>

                                    </div>

                                )
                            )

                        )}

                    </div>

                    {/* ======================================== */}
                    {/* WEEKLY CALLS GRAPH */}
                    {/* ======================================== */}

                    <div className="row mt-3">

                        <div className="col-md-6">

                            <div className="card">

                                {/* Graph Header */}

                                <div className="card-header d-flex align-items-center justify-content-between">

                                    <div>

                                        <h4 className="card-title mb-1">
                                            Weekly Calls
                                        </h4>

                                        <p className="text-muted mb-0">
                                            Total calls and connected
                                            calls for the current week
                                        </p>

                                    </div>

                                </div>

                                {/* Graph Body */}

                                <div className="card-body">

                                    {weeklyCallsLoading ? (

                                        <div
                                            style={{
                                                height: "350px",
                                                display: "flex",
                                                justifyContent:
                                                    "center",
                                                alignItems:
                                                    "center",
                                            }}
                                        >
                                            <LogoSpinnerInline />
                                        </div>

                                    ) : weeklyCalls.length === 0 ? (

                                        <div
                                            style={{
                                                height: "350px",
                                                display: "flex",
                                                justifyContent:
                                                    "center",
                                                alignItems:
                                                    "center",
                                            }}
                                        >

                                            <p className="text-muted mb-0">
                                                No weekly calls data
                                                available
                                            </p>

                                        </div>

                                    ) : (

                                        <div
                                            style={{
                                                width: "100%",
                                                height: "350px",
                                            }}
                                        >

                                            <ResponsiveContainer
                                                width="100%"
                                                height="100%"
                                            >

                                                <LineChart
                                                    data={graphData}
                                                    margin={{
                                                        top: 10,
                                                        right: 20,
                                                        left: 0,
                                                        bottom: 10,
                                                    }}
                                                >

                                                    <CartesianGrid
                                                        strokeDasharray="3 3"
                                                    />

                                                    <XAxis
                                                        dataKey="dayLabel"
                                                    />

                                                    <YAxis
                                                        allowDecimals={
                                                            false
                                                        }
                                                    />

                                                    <Tooltip
                                                        formatter={(
                                                            value: any,
                                                            name: any
                                                        ) => [
                                                                Number(
                                                                    value
                                                                ).toLocaleString(
                                                                    "en-IN"
                                                                ),
                                                                name,
                                                            ]}
                                                        labelFormatter={(
                                                            label: any,
                                                            payload: any[]
                                                        ) => {

                                                            const item =
                                                                payload?.[0]
                                                                    ?.payload;

                                                            if (
                                                                item?.date
                                                            ) {
                                                                return `${label} - ${item.date}`;
                                                            }

                                                            return label;
                                                        }}
                                                    />

                                                    <Legend />

                                                    <Line
                                                        type="monotone"
                                                        dataKey="totalCalls"
                                                        name="Total Calls"
                                                        strokeWidth={3}
                                                        dot={{
                                                            r: 4,
                                                        }}
                                                        activeDot={{
                                                            r: 6,
                                                        }}
                                                    />

                                                    <Line
                                                        type="monotone"
                                                        dataKey="connectedCalls"
                                                        name="Connected Calls"
                                                        strokeWidth={3}
                                                        dot={{
                                                            r: 4,
                                                        }}
                                                        activeDot={{
                                                            r: 6,
                                                        }}
                                                    />

                                                </LineChart>

                                            </ResponsiveContainer>

                                        </div>

                                    )}

                                </div>

                            </div>

                        </div>

                    </div>
                    <div className="row">


                        <div className="col-xxl-12 col-xl-12 d-flex">
                            <div className="card flex-fill">
                                <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
                                    <h4 className="card-title mb-3">Agent Activity</h4>
                                    <div className="d-flex align-items-center me-3 mb-3">
                                        <Select
                                            showSearch
                                            allowClear
                                            placeholder="Select Status"
                                            value={selectedStatus}
                                            onChange={handleStatusChange}
                                            disabled={selectedAgents.length === 0}
                                            style={{ width: 150, borderTopColor: '#fff', borderLeftColor: '#fff', borderRightColor: '#fff' }}
                                            optionFilterProp="label"
                                            filterOption={(input, option) =>
                                                (option?.label ?? "")
                                                    .toLowerCase()
                                                    .includes(input.toLowerCase())
                                            }
                                            options={monitoringStatus.map(group => ({
                                                value: group.id,
                                                label: group.name,
                                            }))}
                                        />
                                        <BiRefresh
                                            className={`refresh-icon ${refreshIsLoading ? "spin" : ""} ms-3`}
                                            onClick={fetchData}
                                        />
                                    </div>
                                </div>
                                {refreshIsLoading ? (
                                    <div
                                        style={{
                                            height: "100%",
                                            width: "100%",
                                            display: "flex",
                                            justifyContent: "center",
                                            alignItems: "center",
                                        }}
                                    >
                                        <LogoSpinnerInline />
                                    </div>
                                ) : (
                                    <div className="card-body px-0">
                                        <div
                                            className="custom-datatable-filter table-responsive"
                                            style={{ maxHeight: "480px", overflowY: "auto" }} // scroll here
                                        >
                                            <table className="table table-hover mb-0">
                                                <thead className="thead-light" style={{ position: "sticky", top: 0, zIndex: 1 }}>
                                                    <tr>
                                                        <th>Action</th>
                                                        <th>Agent Name</th>
                                                        <th>Status</th>
                                                        <th>Session Duration</th>
                                                        <th>Calls</th>
                                                        <th>Connected</th>
                                                        <th>Call Duration</th>
                                                        {/* <th>Idle Time</th> */}
                                                    </tr>
                                                </thead>

                                                <tbody>
                                                    {dashboardData?.map((agent) => (
                                                        <tr key={agent.id} style={{ height: "48px" }}>
                                                            <td>
                                                                <input
                                                                    type="checkbox"
                                                                    checked={selectedAgents.includes(agent.agent)}
                                                                    onChange={() => handleCheckboxChange(agent.agent)}
                                                                    disabled={
                                                                        agent.monitoring_status === "Offline" ||
                                                                        agent.monitoring_status === "On Call"
                                                                    }
                                                                />
                                                            </td>

                                                            <td><div className="d-flex align-items-center">
                                                                <div className="avatar avatar-md">
                                                                    <ImageWithBasePath
                                                                        src={
                                                                            agent.profile_pic
                                                                                ? `${webApi}${agent.profile_pic}`
                                                                                : "/assets/img/logo.png"
                                                                        }
                                                                        className="img-fluid rounded-circle"
                                                                        alt="img"
                                                                    />
                                                                </div><div className="ms-2"><p className="text-dark mb-0">{agent.name}</p></div></div></td>
                                                            <td>
                                                                {(() => {
                                                                    let badgeClass = "badge bg-secondary";

                                                                    if (agent.monitoring_status === "Available") {
                                                                        badgeClass = "badge bg-success";
                                                                    } else if (agent.monitoring_status === "Offline") {
                                                                        badgeClass = "badge bg-danger";
                                                                    } else if (agent.monitoring_status === "Work Assign") {
                                                                        badgeClass = "badge bg-info";
                                                                    }

                                                                    return (
                                                                        <>
                                                                            <span>
                                                                                <i className={`${badgeClass} ti ti-circle-filled fs-9`} />
                                                                            </span>
                                                                            <span>{agent.monitoring_status}</span>
                                                                        </>
                                                                    );
                                                                })()}
                                                            </td>

                                                            <td>
                                                                <Counter updatedAt={agent.updated_at} />
                                                            </td>
                                                            <td>{agent.total_calls}</td>
                                                            <td>{agent.connected_calls}</td>
                                                            <td>{agent.total_call_duration}</td>

                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>


                    </div>
                </div>
            </div>
        </>
    );
};

export default MetaOnBoardDashboard;